import { asHtmlAnnotation } from '@/features/annotation-management'

import {
  ANNOTATION_CONFIDENCE_THRESHOLD,
  ANNOTATION_CONTEXT_LENGTH,
} from '../constants'
import type { AnnotationMetadata, SearchResult } from '../types'

export class DomLocatorService {
  constructor(private readonly root: Document | Element) {}

  private getOwnerDocument(): Document {
    if (this.root instanceof Document) {
      return this.root
    }
    return this.root.ownerDocument ?? document
  }

  private getRootElement(): Element | Document {
    return this.root instanceof Document ? this.root.body : this.root
  }

  private isRootNode(node: Node, rootElement: Element | Document): boolean {
    return node === rootElement || node === this.root
  }

  private getParentPath(parent: Node, rootElement: Element | Document): string {
    if (this.isRootNode(parent, rootElement)) {
      return '.'
    }
    return this.generateXPath(parent)
  }

  private buildTextNodeXPath(parentPath: string, index: number): string {
    if (parentPath === '.') {
      return `./text()[${index + 1}]`
    }
    return `${parentPath}/text()[${index + 1}]`
  }

  private generateTextNodeXPathWithHighlight(
    node: Text,
    highlightSpan: Element,
    rootElement: Element | Document
  ): string {
    const grandParent = highlightSpan.parentNode
    if (!grandParent) return ''

    const textNodesInGrandParent =
      this.getTextNodesSkippingHighlights(grandParent)
    const index = textNodesInGrandParent.indexOf(node)

    if (index === -1) return ''

    const grandParentPath = this.getParentPath(grandParent, rootElement)
    return this.buildTextNodeXPath(grandParentPath, index)
  }

  private generateTextNodeXPathWithoutHighlight(
    node: Text,
    parent: Node,
    rootElement: Element | Document
  ): string {
    const textNodesInParent = this.getTextNodesSkippingHighlights(parent)
    const index = textNodesInParent.indexOf(node)

    if (index === -1) return ''

    const parentPath = this.getParentPath(parent, rootElement)
    return this.buildTextNodeXPath(parentPath, index)
  }

  private generateTextNodeXPath(
    node: Text,
    rootElement: Element | Document
  ): string {
    const parent = node.parentNode
    if (!parent) return ''

    const parentElement = parent as Element
    if (parentElement.classList?.contains('annotation-highlight')) {
      return this.generateTextNodeXPathWithHighlight(
        node,
        parentElement,
        rootElement
      )
    }

    return this.generateTextNodeXPathWithoutHighlight(node, parent, rootElement)
  }

  private getElementSiblingIndex(
    element: Element,
    parent: Node
  ): { index: number; tagName: string } {
    const siblings = Array.from(parent.childNodes).filter(
      (n) =>
        (n as Element).nodeType === Node.ELEMENT_NODE &&
        (n as Element).tagName === element.tagName
    ) as Element[]
    const index = siblings.indexOf(element) + 1
    const tagName = element.tagName.toLowerCase()
    return { index, tagName }
  }

  private generateElementXPathForRootParent(element: Element): string {
    const children =
      this.root instanceof Document
        ? this.root.body.children
        : this.root.children
    const siblings = Array.from(children).filter(
      (el) => el.tagName === element.tagName
    )
    const index = siblings.indexOf(element) + 1
    const tagName = element.tagName.toLowerCase()
    return `./${tagName}[${index}]`
  }

  private generateElementXPathForRegularParent(
    element: Element,
    parent: Node,
    rootElement: Element | Document
  ): string {
    const { index, tagName } = this.getElementSiblingIndex(element, parent)

    if (parent === rootElement) {
      return `./${tagName}[${index}]`
    }

    const parentPath = this.generateXPath(parent)
    if (!parentPath || parentPath === '.') {
      return `./${tagName}[${index}]`
    }

    return `${parentPath}/${tagName}[${index}]`
  }

  private generateElementXPath(
    element: Element,
    rootElement: Element | Document
  ): string {
    const parent = element.parentNode
    if (!parent) return ''

    if (parent === this.root) {
      return this.generateElementXPathForRootParent(element)
    }

    return this.generateElementXPathForRegularParent(
      element,
      parent,
      rootElement
    )
  }

  generateXPath(node: Node): string {
    const rootElement = this.getRootElement()

    if (this.isRootNode(node, rootElement)) {
      return '.'
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return this.generateTextNodeXPath(node as Text, rootElement)
    }

    return this.generateElementXPath(node as Element, rootElement)
  }

  resolveXPath(xpath: string): Node | null {
    try {
      const contextNode =
        this.root instanceof Document ? this.root : this.getRootElement()

      const textNodePattern = /^(.+)\/text\(\)\[(\d+)]$/
      const match = textNodePattern.exec(xpath)

      if (match) {
        const [, parentPath, indexStr] = match
        const index = Number.parseInt(indexStr, 10)

        const parentNode =
          parentPath === '.'
            ? contextNode
            : this.resolveXPathElement(parentPath, contextNode)

        if (parentNode) {
          const textNodes = this.getTextNodesSkippingHighlights(parentNode)
          const targetNode = textNodes[index - 1]
          return targetNode || null
        }
      }

      const ownerDoc = this.getOwnerDocument()
      const result = ownerDoc.evaluate(
        xpath,
        contextNode,
        null,
        XPathResult.FIRST_ORDERED_NODE_TYPE,
        null
      )
      return result.singleNodeValue
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to resolve XPath:', e)
      return null
    }
  }

  private resolveXPathElement(xpath: string, contextNode: Node): Node | null {
    try {
      const ownerDoc = this.getOwnerDocument()
      const result = ownerDoc.evaluate(
        xpath,
        contextNode,
        null,
        XPathResult.FIRST_ORDERED_NODE_TYPE,
        null
      )
      return result.singleNodeValue
    } catch {
      return null
    }
  }

  resolveShadowNode(node: Node): Node {
    let currentNode: Node | null = node

    while (currentNode) {
      const parent: Node | null = currentNode.parentNode

      if (!parent) {
        break
      }

      if ((parent as ShadowRoot).host) {
        return (parent as ShadowRoot).host
      }

      currentNode = parent
    }

    return node
  }

  findRangesByText(text: string, root?: Node): Range[] {
    const matches: Range[] = []
    const normalizedText = this.normalizeWhitespace(text)
    const searchRoot = root || this.getRootElement()
    const ownerDoc = this.getOwnerDocument()

    const walker = ownerDoc.createTreeWalker(
      searchRoot,
      NodeFilter.SHOW_TEXT,
      null
    )

    while (walker.nextNode()) {
      const node = walker.currentNode
      const nodeText = this.normalizeWhitespace(node.textContent || '')

      let startIndex = 0
      while (
        (startIndex = nodeText.indexOf(normalizedText, startIndex)) !== -1
      ) {
        try {
          const range = ownerDoc.createRange()
          range.setStart(node, startIndex)
          range.setEnd(node, startIndex + normalizedText.length)
          matches.push(range)
        } catch {
          // do nothing
        }
        startIndex += normalizedText.length
      }
    }

    return matches
  }

  scoreWithContext(
    range: Range,
    expectedPrefix: string,
    expectedSuffix: string
  ): number {
    let score = 0.5

    const actualPrefix = this.getTextBefore(range, ANNOTATION_CONTEXT_LENGTH)
    const actualSuffix = this.getTextAfter(range, ANNOTATION_CONTEXT_LENGTH)

    const normPrefix = this.normalizeWhitespace(actualPrefix)
    const normExpectedPrefix = this.normalizeWhitespace(expectedPrefix)
    const normSuffix = this.normalizeWhitespace(actualSuffix)
    const normExpectedSuffix = this.normalizeWhitespace(expectedSuffix)

    if (normPrefix.includes(normExpectedPrefix)) {
      score += 0.25
    }

    if (normSuffix.includes(normExpectedSuffix)) {
      score += 0.25
    }

    return score
  }

  extractContext(
    range: Range,
    length: number = ANNOTATION_CONTEXT_LENGTH
  ): { prefix: string; suffix: string } {
    const startContainer = range.startContainer
    const endContainer = range.endContainer

    let prefix = ''
    if (startContainer.nodeType === Node.TEXT_NODE) {
      const text = startContainer.textContent || ''
      const start = Math.max(0, range.startOffset - length)
      prefix = text.substring(start, range.startOffset).trim()
    }

    let suffix = ''
    if (endContainer.nodeType === Node.TEXT_NODE) {
      const text = endContainer.textContent || ''
      const end = Math.min(text.length, range.endOffset + length)
      suffix = text.substring(range.endOffset, end).trim()
    }

    return { prefix, suffix }
  }

  private getTextBefore(range: Range, length: number): string {
    const container = range.startContainer
    if (container.nodeType !== Node.TEXT_NODE) return ''

    const text = container.textContent || ''
    const start = Math.max(0, range.startOffset - length)
    return text.substring(start, range.startOffset)
  }

  private getTextAfter(range: Range, length: number): string {
    const container = range.endContainer
    if (container.nodeType !== Node.TEXT_NODE) return ''

    const text = container.textContent || ''
    const end = Math.min(text.length, range.endOffset + length)
    return text.substring(range.endOffset, end)
  }

  private normalizeWhitespace(text: string): string {
    return text.replace(/\s+/g, ' ').trim() // NOSONAR - normalize whitespace
  }

  private addTextNodeIfValid(node: Node, textNodes: Text[]): void {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim()
      if (text) {
        textNodes.push(node as Text)
      }
    }
  }

  private processHighlightElement(element: Element, textNodes: Text[]): void {
    for (const highlightChild of Array.from(element.childNodes)) {
      this.addTextNodeIfValid(highlightChild, textNodes)
    }
  }

  private processChildNode(child: Node, textNodes: Text[]): void {
    if (child.nodeType === Node.TEXT_NODE) {
      this.addTextNodeIfValid(child, textNodes)
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const element = child as Element
      if (element.classList?.contains('annotation-highlight')) {
        this.processHighlightElement(element, textNodes)
      }
    }
  }

  private getTextNodesSkippingHighlights(parent: Node): Text[] {
    const textNodes: Text[] = []

    for (const child of Array.from(parent.childNodes)) {
      this.processChildNode(child, textNodes)
    }

    return textNodes
  }

  getTextNodesInRange(range: Range): Text[] {
    const textNodes: Text[] = []
    const root = range.commonAncestorContainer
    const ownerDoc = this.getOwnerDocument()

    if (root.nodeType === Node.TEXT_NODE) {
      if (root.textContent?.trim()) {
        return [root as Text]
      }
      return []
    }

    const walker = ownerDoc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node: Node) => {
        try {
          return range.intersectsNode(node)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT
        } catch {
          return NodeFilter.FILTER_REJECT
        }
      },
    })

    let node: Node | null
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        textNodes.push(node as Text)
      }
    }

    return textNodes
  }

  getTextNodeRangeIntersection(range: Range, textNode: Text): Range | null {
    const len = textNode.length

    let startOffset: number
    if (range.startContainer === textNode) {
      startOffset = range.startOffset
    } else {
      startOffset = 0
    }

    let endOffset: number
    if (range.endContainer === textNode) {
      endOffset = range.endOffset
    } else {
      endOffset = len
    }

    if (startOffset >= endOffset) {
      return null
    }

    const ownerDoc = this.getOwnerDocument()
    const intersection = ownerDoc.createRange()
    intersection.setStart(textNode, startOffset)
    intersection.setEnd(textNode, endOffset)

    return intersection
  }

  searchByXPath(annotation: AnnotationMetadata): Range | null {
    const htmlAnnotation = asHtmlAnnotation(annotation)
    if (!htmlAnnotation?.fragments || htmlAnnotation.fragments.length === 0) {
      return null
    }

    const firstFragment = htmlAnnotation.fragments[0]
    const lastFragment =
      htmlAnnotation.fragments[htmlAnnotation.fragments.length - 1]

    const startNode = this.resolveXPath(firstFragment.startPath)
    const endNode = this.resolveXPath(lastFragment.endPath)

    if (!startNode || !endNode) {
      return null
    }

    if (
      startNode.nodeType !== Node.TEXT_NODE ||
      endNode.nodeType !== Node.TEXT_NODE
    ) {
      return null
    }

    const startTextLength = (startNode as Text).length
    const endTextLength = (endNode as Text).length

    if (
      firstFragment.startOffset > startTextLength ||
      lastFragment.endOffset > endTextLength
    ) {
      return null
    }

    try {
      const ownerDoc = this.getOwnerDocument()
      const range = ownerDoc.createRange()
      range.setStart(startNode, firstFragment.startOffset)
      range.setEnd(endNode, lastFragment.endOffset)

      if (range.toString().trim() === annotation.text.trim()) {
        return range
      }
    } catch {
      return null
    }

    return null
  }

  findBestMatchWithContext(
    matches: Range[],
    expectedPrefix: string,
    expectedSuffix: string
  ): SearchResult | null {
    const scored = matches.map((range) => ({
      range,
      score: this.scoreWithContext(range, expectedPrefix, expectedSuffix),
    }))

    scored.sort((a, b) => b.score - a.score)

    const bestMatch = scored[0]

    if (!bestMatch || bestMatch.score < ANNOTATION_CONFIDENCE_THRESHOLD) {
      return null
    }

    return {
      range: bestMatch.range,
      confidence: bestMatch.score,
      method: 'text-context',
    }
  }

  applyViaFragments(
    annotation: AnnotationMetadata
  ): Array<{ range: Range; index: number }> {
    const htmlAnnotation = asHtmlAnnotation(annotation)
    if (
      !htmlAnnotation ||
      !htmlAnnotation.fragments ||
      htmlAnnotation.fragments.length === 0
    ) {
      return []
    }

    const ranges: Array<{ range: Range; index: number }> = []

    htmlAnnotation.fragments.forEach((fragment, index) => {
      const startNode = this.resolveXPath(fragment.startPath)
      const endNode = this.resolveXPath(fragment.endPath)

      if (!startNode || !endNode) {
        return
      }

      if (startNode.nodeType !== Node.TEXT_NODE) {
        return
      }

      if (endNode.nodeType !== Node.TEXT_NODE) {
        return
      }

      const startTextLength = (startNode as Text).length
      const endTextLength = (endNode as Text).length

      if (fragment.startOffset > startTextLength) {
        return
      }

      if (fragment.endOffset > endTextLength) {
        return
      }

      const expectedText = fragment.text
      const actualText =
        startNode === endNode
          ? (startNode as Text).textContent?.substring(
              fragment.startOffset,
              fragment.endOffset
            )
          : null

      if (actualText !== null && actualText !== expectedText) {
        return
      }

      try {
        const ownerDoc = this.getOwnerDocument()
        const range = ownerDoc.createRange()
        range.setStart(startNode, fragment.startOffset)
        range.setEnd(endNode, fragment.endOffset)

        if (!range.collapsed) {
          ranges.push({ range, index })
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(
          '[DomLocator] fragment',
          index,
          'error building range',
          err
        )
      }
    })

    return ranges
  }
}
